import React, { useState, useEffect, useContext }  from 'react';
import styles from 'components/layout_and_quote_builder/_style.module.scss';
import LayoutNavSectionCombinationList from 'components/layout_and_quote_builder/LayoutNavSectionCombinationList.jsx';
import SelectedList from 'components/layout_and_quote_builder/quote_builder/SelectedList.jsx';
import LayoutContext from 'components/contexts/LayoutContext.jsx';
import ChooseLayoutDimensions from 'components/layout_and_quote_builder/options/ChooseLayoutDimensions.jsx';
import GeneralNavSection from 'components/layout_and_quote_builder/quote_builder/GeneralNavSection.jsx';
import { shouldShowSectionLabel } from 'components/layout_and_quote_builder/utils/navSectionUtils.js';

const LayoutAndQuoteBuilderNavSection = ( props ) => {
  const myLayoutContext = useContext(LayoutContext);

  // const fallbackMaterialValues = () => {
  //   return {
  //     layout_dimension_ids: [21],
  //     layout_dimension_unit: myLayoutContext.layoutOptions.layout_dimension_unit,
  //     layout_dimension_top_margin_diecutting: myLayoutContext.layoutOptions.layout_dimension_top_margin,
  //     layout_dimension_side_margin_diecutting: myLayoutContext.layoutOptions.layout_dimension_side_margin,
  //     layout_dimension_bottom_margin_diecutting: myLayoutContext.layoutOptions.layout_dimension_bottom_margin,

  //     layout_dimension_top_margin_printing: myLayoutContext.layoutOptions.layout_dimension_top_margin,
  //     layout_dimension_side_margin_printing: myLayoutContext.layoutOptions.layout_dimension_side_margin,
  //     layout_dimension_bottom_margin_printing: myLayoutContext.layoutOptions.layout_dimension_bottom_margin,
  //   }
  // }

  // Use props if provided, otherwise use local state (for backwards compatibility)
  const selectedList = props.selectedList !== undefined ? props.selectedList : [];
  const setSelectedList = props.setSelectedList || (() => {});

  const [materialLayoutDimensions, setMaterialLayoutDimensions] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [manuallyEnabled, setManuallyEnabled] = useState(false);

  // The bobbin checkbox is an input to the material-sheet finder, so re-fetching with the toggle value
  // recomputes "Chosen material dimensions" (bobbin true/false changes the result). Absent on mount ->
  // the part_type default.
  const fetchDefaultMaterialValues = async (bobbinEnabled) => {
    if(props.navSection.index == undefined) {
      return ;
    }
    try {
      const bobbinQuery = bobbinEnabled === undefined ? '' : `?bobbin_enabled=${bobbinEnabled}`;
      const response = await fetch(
        `/manufacturing-cost-calculator/${window.user_quote_id}/default-material-values/${props.navSection.index}${bobbinQuery}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch default material values');
      }
      const data = await response.json();
      // Merge so a re-fetch on toggle refreshes the computed sheet without dropping the bobbin_enabled /
      // filtering flags ChooseLayoutDimensions writes into this object for the combinations POST.
      setMaterialLayoutDimensions((previous) => ({ ...(previous || {}), ...data }));
    } catch (error) {
      console.error('Error fetching default material values:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch default material values from API on mount (part_type default bobbin).
  useEffect(() => {
    fetchDefaultMaterialValues(undefined);
  }, []);

  // If this section's (inherited) solution is cleared, re-gate it so its enable button reappears,
  // rather than relying on the section being remounted. Harmless for non-linked sections, which do
  // not use manuallyEnabled.
  useEffect(() => {
    if (selectedList.length === 0) {
      setManuallyEnabled(false);
    }
  }, [selectedList.length]);

  const navSectionLabel = () => {
    if(props.navSection.label) {
      return <span className={styles.navSectionLabel}> {props.navSection.label} </span>
    }
  }

  const chooseLayoutDimensionsComponent = () => {
    return <ChooseLayoutDimensions
      defaultValues={materialLayoutDimensions}
      publicSetter={setMaterialLayoutDimensions}
      onBobbinChange={(value) => fetchDefaultMaterialValues(value)}
      index={props.index}
    />
  }

  // Whether to show the material sheet finder's admin sanity displays (the resolved part_type and the
  // chosen material sheets). Admins always see them; users in ENV['quote_builder_v2_enabled_users'] are
  // treated like admins on this Manufacturing Cost Calculator page for now, so they see them too. The
  // flag is undefined on pages that do not set it, which correctly keeps these displays hidden there.
  const canSeeSectionInsights = window.current_user_admin || window.quote_builder_v2_enabled;

  // Append the section's part_type to its title (e.g. "Corrugated cardboard
  // (corrugated_cardboard_flexo)"), so a reviewer can see exactly which part_type a section resolved to
  // and flag anything wrong. Hidden from customers.
  const adminPartTypeSuffix = () => {
    if (!canSeeSectionInsights || !props.navSection.part_type) {
      return null;
    }

    return (
      <span style={{ fontSize: '14px', color: '#888', fontWeight: 'normal' }}>
        {' '}({props.navSection.part_type})
      </span>
    );
  }

  // A single material-sheet chip (e.g. "B1 707mm X 1000mm" or the "Üretilemiyor" indicator).
  const materialDimensionChip = (text, key) => (
    <div
      key={key}
      style={{
        fontSize: '13px',
        color: '#555',
        border: '1px solid #ccc',
        borderRadius: '4px',
        padding: '4px 12px',
        backgroundColor: '#f9f9f9',
      }}
    >
      {text}
    </div>
  );

  // Sanity check: the material sheet dimensions computed for this section are hidden from customers,
  // but shown to admins (and quote-builder-v2 users, per canSeeSectionInsights) next to the section
  // heading so they can eyeball the chosen sheets and flag anything that looks off. The sheets are
  // grouped by part (e.g. "Sleeve cover", "Inner tray", "Sleeve cover, Inner tray") so a section with an
  // oversize combined nesting still shows the producible per-part sheets instead of one whole-section
  // "cannot produce". Falls back to the flat list for any endpoint that does not send groups. Nothing
  // renders when the viewer is not permitted or when nothing was computed.
  const adminChosenMaterialDimensionsLine = () => {
    if (!canSeeSectionInsights) {
      return null;
    }

    const groups = materialLayoutDimensions?.material_sheet_groups || [];
    const flatSheets = materialLayoutDimensions?.layout_dimensions || [];
    const renderableGroups = groups.filter((group) => group.dimensions && group.dimensions.length > 0);
    const hasGroups = renderableGroups.length > 0;

    if (!hasGroups && flatSheets.length === 0) {
      return null;
    }

    return (
      <center>
        <div
          style={{ marginTop: '-10px', marginBottom: '10px' }}
          title="The material sheet dimensions computed for this section, per part. Flag if anything looks off."
        >
          <div style={{ fontSize: '12px', color: '#888', marginBottom: '6px' }}>
            Chosen material dimensions:
          </div>

          {hasGroups ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
              {renderableGroups.map((group, groupIndex) => {
                const partLabel = (group.part_names && group.part_names.length > 0)
                  ? group.part_names.join(', ')
                  : group.label;

                return (
                  <div
                    key={partLabel || groupIndex}
                    style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}
                  >
                    {partLabel && (
                      <div style={{ fontSize: '12px', color: '#555', fontWeight: 'bold' }}>
                        {partLabel}
                      </div>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        gap: '6px',
                      }}
                    >
                      {group.dimensions.map((sheet, sheetIndex) => materialDimensionChip(sheet.description, `${sheet.description}-${sheetIndex}`))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
              {flatSheets.map((sheet, sheetIndex) => materialDimensionChip(sheet.description, sheetIndex))}
            </div>
          )}
        </div>
      </center>
    );
  }

  if(props.navSection.part_type == "general") {
    return <GeneralNavSection totalNavSectionSize={props.totalNavSectionSize} />
  }

  // Show loading state while fetching default material values
  if (isLoading || !materialLayoutDimensions) {
    return (
      <div className={styles.LayoutAndQuoteBuilderNavSection}>
        <div className={styles.nav_section}>
          {shouldShowSectionLabel(props.totalNavSectionSize, props.navSection.label) && (
            <center> <h2> { props.navSection.label} </h2> </center>
          )}
          <div style={{ textAlign: 'center', padding: '20px' }}>
            {t('quote_builder.loading')}...
          </div>
        </div>
      </div>
    );
  }

  // A linked section (e.g. corrugated_cardboard_offset) inherits its solution from a parent section
  // (carton_lamination). It stays visible but gated behind its own "Start Working" button until the
  // parent's solution is inherited (selectedList populated), so the user enables it deliberately.
  const userQuoteNavSection = window.user_quote_nav_sections?.find(
    section => section.sort_order === props.navSection.index
  );
  const isLinkedSection = !!userQuoteNavSection?.solution_user_quote_nav_section_id;
  const parentSolutionReady = selectedList.length > 0;

  const sectionBody = () => (
    <>
      { selectedList.length == 0 && <LayoutNavSectionCombinationList
        navSection={props.navSection}
        index={props.index}
        count={props.count}
        setSelectedList={setSelectedList}
        materialLayoutDimensions={materialLayoutDimensions}
      /> }

      <SelectedList selectedList={selectedList} setSelectedList={setSelectedList} config={props.config} index={props.index} totalNavSectionSize={props.totalNavSectionSize}/>
    </>
  );

  const enableButton = () => (
    <div style={{ textAlign: 'center' }}>
      {!parentSolutionReady && <p>{ t('quote_builder.this_section_not_yet_active') }</p>}
      <button
        onClick={() => setManuallyEnabled(true)}
        disabled={!parentSolutionReady}
        className="btn simple-btn"
      >
        { t('quote_builder.start_working_on_this_section') }
      </button>
      <hr />
    </div>
  );

  return (
    <>
    <div className={styles.LayoutAndQuoteBuilderNavSection}>

      <div className={styles.nav_section}>

        {shouldShowSectionLabel(props.totalNavSectionSize, props.navSection.label) && (
          <center> <h2> { props.navSection.label}{adminPartTypeSuffix()} </h2> </center>
        )}

        {/* The bobbin checkbox is an INPUT to the material-sheet finder, so it sits ABOVE the resulting
            "Chosen material dimensions" line, which recomputes when the checkbox toggles. */}
        <div className={styles.nav_section_layout_selector}> <span> {chooseLayoutDimensionsComponent()} </span> </div>

        {adminChosenMaterialDimensionsLine()}

        { isLinkedSection && !(parentSolutionReady && manuallyEnabled) ? enableButton() : sectionBody() }

      </div>
    </div>
    <br />
    <br />
    </>
  )
}

export default LayoutAndQuoteBuilderNavSection;
